import { Box, Theme, ToggleButton, toggleButtonClasses, ToggleButtonGroup, toggleButtonGroupClasses, Typography, useMediaQuery } from '@mui/material';
import AppLayout from '../Components/AppLayout';
import { makeStyles, styled } from '@mui/styles';
import { useState, useEffect, useContext } from 'react';
import { AxiosResponse } from 'axios';
import { getMeetings } from '../DashBoard/Api';
import Loaders from '../../../GlobalComponents/KenLoader';
import StartupTable from '../Components/StartupTable';
import { useQuery } from '@tanstack/react-query';
import storageService from '../../../Utils/storageService';
import { AuthContext } from '../../../Helpers/Contexts/authContext';

const useStyles = makeStyles(() => ({
  title: {
    fontSize: '14px !important',
    fontWeight: '500 !important',
    '@media (min-width: 1920px)': {
      fontSize: '18px !important',
    },
  },
  cardBgs: {
    borderRadius: '8px',
    border: '1px solid #E6E6E6',
    background: '#fff',
    padding: '20px',
    overflow: 'auto',
    '@media (max-width: 600px)': {
      background: 'transparent',
      border: 'none',
      padding: '0px',
    },
  },
}));

const AppToggleButton = styled(ToggleButtonGroup)(({ theme }: { theme: Theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0),
  },
  [`& .${toggleButtonClasses.root}`]: {
    '&.Mui-selected': {
      background: `linear-gradient(275.2deg, #5F439D 0%, #8167B8 124.79%)`,
      color: '#fff',
      '&:hover': {
        background: `linear-gradient(275.2deg, #5F439D 0%, #8167B8 124.79%)`,
        color: '#fff',
      },
    },
    borderRadius: '8px !important',
    color: '#604797',
    background: '#fff',
    border: '1px solid #E6E6E6',
    '&:hover': {
      borderRadius: '8px !important',
      color: '#604797',
      background: '#fff',
      border: '1px solid #E6E6E6',
    },
  },
}));

const buttonsArray = ['Current Evaluation', 'Completed Evaluation'];
const buttonsArrayII = ['Current', 'Completed'];

const AssignedStartUps: React.FunctionComponent = () => {
  const classes = useStyles();
  const [contactId, setContactId] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const isMaxScreen = useMediaQuery('(min-width:1920px)');
  const { state: { user } } = useContext(AuthContext);

  const [toggleValue, setToggleValue] = useState<string>(isMobile ? 'Current' : 'Current Evaluation');
  const [startupData, setStartupData] = useState<any>({
    Current: [],
    Completed: [],
    'Current Evaluation': [],
    'Completed Evaluation': [],
  });

  // Load contactId from storage on component mount
  useEffect(() => {
    const loadStorageData = async () => {
      try {
        const contactIdData = await storageService.getItem('contactId');
        setContactId(contactIdData);
      } catch (error) {
        console.error('Error loading storage data:', error);
      }
    };
    loadStorageData();
  }, []);

  const handleChange = (value: string) => {
    setToggleValue(value);
  };

  const getMeetingData = useQuery({
    queryKey: ['getMeeting', user?.accountId],
    queryFn: async () => {
      const response: AxiosResponse = await getMeetings(user?.accountId || '');

      const currentDate = new Date();
      const assignments = response?.data?.data?.assignments;
      const meetings = response?.data?.data?.meetings;
      const upcomingMeetings = meetings.filter((meeting: any) => {
        const startDate: any = new Date(meeting.End_Date_Time__c);
        return startDate >= currentDate;
      });

      if (assignments) {
        const customData: any = {
          Current: [],
          Completed: [],
          'Current Evaluation': [],
          'Completed Evaluation': [],
        };
        
        for (let i = 0; i < assignments.length; i++) {
          const element = assignments[i];
          const getMeeting = upcomingMeetings.find((items: any) => {
            return items?.Application__c === element?.Application__c;
          });

          if (element?.Evaluation_Scores__r?.records[0]?.Evaluated__c === true) {
            customData['Completed'].push({ ...element, ...getMeeting });
            customData['Completed Evaluation'].push({ ...element, ...getMeeting });
          } else {
            customData['Current'].push({ ...element, ...getMeeting });
            customData['Current Evaluation'].push({ ...element, ...getMeeting });
          }
        }

        setStartupData(customData);
      }

      return response;
    },
    refetchOnWindowFocus: false,
    enabled: !!user?.accountId,
  });

  return (
    <AppLayout>
      {getMeetingData.isLoading && <Loaders />}
      <Box
        component={'div'}
        minHeight={isMaxScreen ? '892px' : '669px'}
        maxHeight={isMaxScreen ? '892px' : '669px'}
        className={`${classes.cardBgs} d-flex flex-column`}
        gap={isMobile ? '10px' : '20px'}
      >
        <Typography className={classes.title} color={'#333333'}>
          Assigned Startups
        </Typography>
        <Box component={'div'} mt={2}>
          <AppToggleButton
            onChange={(event, value) => {
              if (value !== null) {
                handleChange(value);
              }
            }}
            sx={{ gap: isMobile ? '3px' : '6px', padding: isMobile ? '4px' : '8px', border: '1px solid #E6E6E6', borderRadius: isMobile ? '8px' : '12px' }}
            value={toggleValue}
            exclusive
            size="small"
          >
            {(isMobile ? buttonsArrayII : buttonsArray).map((items, index) => {
              return (
                <ToggleButton
                  key={index}
                  size="small"
                  sx={{ padding: isMobile ? '6px 10px' : '10px 14px', fontSize: isMobile ? '12px' : isMaxScreen ? '18px' : '14px', textTransform: 'capitalize' }}
                  value={items}
                >
                  {items}
                </ToggleButton>
              );
            })}
          </AppToggleButton>
        </Box>
        <Box mt={2} width={'85%'}>
          <StartupTable 
            data={startupData[toggleValue] || []}
            showPagination={true}
            rowsPerPageOptions={[5, 10, 25]}
            defaultRowsPerPage={10}
          />
        </Box>
      </Box>
    </AppLayout>
  );
};

export default AssignedStartUps;
